import type { Edge, Node } from "@xyflow/react";
import {
  firstGivenName,
  type CompanyAcquisition,
  type CompanyDTO,
  type Nullable,
} from "@shaarp/common";

// ─── Types ────────────────────────────────────────────────────────────

/** Maps handle id -> { top%, left% } for per-edge handle positioning */
export type HandlePositions = Record<string, { top: number; left: number }>;

export type CentralNodeData = {
  label: string;
  nodeKind: "central";
  handlePositions?: HandlePositions;
};

export type PersonNodeData = {
  label: string;
  nodeKind: "person";
  role: Nullable<string>;
  handlePositions?: HandlePositions;
};

export type CompanyNodeData = {
  label: string;
  nodeKind: "company";
  relationshipType: RelationshipType;
  siren: Nullable<string>;
  role: Nullable<string>;
  handlePositions?: HandlePositions;
};

export type RelationshipType =
  | "mandate"
  | "parent"
  | "acquisition"
  | "affiliated"
  | "domiciliation";

export type SpiderNode = Node<CentralNodeData | PersonNodeData | CompanyNodeData>;

export type PersonEntry = {
  key: string;
  firstName: string;
  lastName: string;
  role: Nullable<string>;
  mandateCompanies: { siren: Nullable<string>; name: string; role: Nullable<string> }[];
};

export type GraphData = { nodes: SpiderNode[]; edges: Edge[] };

export type CompanyRow = {
  name: string;
  type: RelationshipType;
  role: Nullable<string>;
  siren: Nullable<string>;
  detail: Nullable<string>;
};

// ─── Constants ────────────────────────────────────────────────────────

/** Every person in the graph is an executive: one colour for them all. */
export const PERSON_COLOR = "#3b82f6";

export const RELATIONSHIP_COLORS: Record<RelationshipType, string> = {
  mandate: "#6b7280",
  parent: "#10b981",
  acquisition: "#f59e0b",
  affiliated: "#6b7280",
  domiciliation: "#ec4899",
};

export const CENTRAL_SIZE = 120;
export const PERSON_SIZE = 80;
export const COMPANY_NODE_WIDTH = 170;
export const COMPANY_NODE_HEIGHT = 50;

export const MAX_PERSONS = 12;
export const MAX_MANDATES_PER_PERSON = 5;
export const MAX_ACQUISITIONS = 8;
export const MAX_AFFILIATES = 6;

// ─── Private Helpers ──────────────────────────────────────────────────

/** Safely extract a displayable string from parentCompany (may be string or object from Coresignal). */
function resolveParentCompanyName(
  value: unknown,
): string | null {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "parent_company_name" in value) {
    return (value as { parent_company_name: string }).parent_company_name;
  }
  return null;
}

// ─── Pure Functions ───────────────────────────────────────────────────

/** Keyed on the first given name: an executive lists them all, the other sources one. */
export function normalizeKey(firstName: string, lastName: string): string {
  return `${firstGivenName(firstName).toLowerCase()}|${lastName.trim().toLowerCase()}`;
}

/** Returns true if the executive is a legal entity (personne morale) rather than a person. */
export function isLegalEntity(exec: {
  firstName: string;
  lastName: string;
  isLegalEntity?: boolean;
}): boolean {
  // Only a very old row lacks the flag: it then tells by its empty first name.
  return exec.isLegalEntity ?? exec.firstName.trim() === "";
}

/**
 * Builds person registry by cross-referencing executives and executive
 * mandates. Persons are matched by normalized firstName|lastName.
 * Excludes legal entities (personnes morales) — they are handled separately.
 */
export function buildPersonRegistry(company: CompanyDTO): PersonEntry[] {
  const registry = new Map<string, PersonEntry>();

  // 1. Seed from executives (physical persons only)
  for (const exec of company.business.executives ?? []) {
    if (isLegalEntity(exec)) continue;
    const key = normalizeKey(exec.firstName, exec.lastName);
    registry.set(key, {
      key,
      firstName: exec.firstName,
      lastName: exec.lastName,
      role: exec.role,
      mandateCompanies: [],
    });
  }

  // 2. Attach mandate companies from executiveMandates
  const rel = company.corporateRelationships;
  for (const mandate of rel?.executiveMandates ?? []) {
    const key = normalizeKey(mandate.executiveFirstName, mandate.executiveLastName);
    let person = registry.get(key);
    if (!person) {
      // Person only known via mandates
      person = {
        key,
        firstName: mandate.executiveFirstName,
        lastName: mandate.executiveLastName,
        role: mandate.executiveRole,
        mandateCompanies: [],
      };
      registry.set(key, person);
    }
    for (const mc of mandate.companies) {
      person.mandateCompanies.push({
        siren: mc.siren,
        name: mc.name,
        role: mc.role,
      });
    }
  }

  // Sort by number of mandates (most connected first), then limit
  return Array.from(registry.values())
    .sort((a, b) => b.mandateCompanies.length - a.mandateCompanies.length)
    .slice(0, MAX_PERSONS);
}

export function buildSpiderGraph(company: CompanyDTO): GraphData {
  const nodes: SpiderNode[] = [];
  const edges: Edge[] = [];
  const rel = company.corporateRelationships;

  // Central node
  nodes.push({
    id: "center",
    type: "central",
    data: { label: company.name, nodeKind: "central" as const },
    position: { x: 0, y: 0 },
  });

  // Build person registry + holding companies
  const persons = buildPersonRegistry(company);
  const holdingCompanies = rel?.holdingCompanies ?? [];

  // Track company nodes already created (by siren or name key) to deduplicate
  const companyNodeKeys = new Map<string, string>(); // key -> nodeId

  // Add holding companies as company nodes linked to center
  holdingCompanies.forEach((hc, i) => {
    const hcId = `holding-${i}`;
    nodes.push({
      id: hcId,
      type: "company",
      data: {
        label: hc.name,
        nodeKind: "company" as const,
        relationshipType: "parent" as const,
        siren: hc.siren,
        role: hc.role,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: `e-holding-${i}`,
      source: hcId,
      target: "center",
      label: hc.role ?? undefined,
      labelStyle: { fontSize: 9, fill: "#64748b" },
      style: { stroke: RELATIONSHIP_COLORS.parent, strokeWidth: 2.5 },
    });
    companyNodeKeys.set(hc.name.toLowerCase(), hcId);
  });

  // Add person nodes + their mandate company nodes
  persons.forEach((person, pIdx) => {
    const personId = `person-${pIdx}`;
    nodes.push({
      id: personId,
      type: "person",
      data: {
        label: `${person.firstName} ${person.lastName}`,
        nodeKind: "person" as const,
        role: person.role,
      },
      position: { x: 0, y: 0 },
    });

    // Edge: center -> person, labelled with the role
    edges.push({
      id: `e-center-${personId}`,
      source: "center",
      target: personId,
      label: person.role ?? "",
      labelStyle: { fontSize: 9, fill: "#64748b" },
      style: { stroke: PERSON_COLOR, strokeWidth: 2 },
    });

    // Mandate companies (limited)
    const mandates = person.mandateCompanies.slice(0, MAX_MANDATES_PER_PERSON);
    mandates.forEach((mc, mIdx) => {
      const companyKey = mc.siren ?? mc.name.toLowerCase();
      let companyNodeId = companyNodeKeys.get(companyKey);

      if (!companyNodeId) {
        companyNodeId = `company-${pIdx}-${mIdx}`;
        companyNodeKeys.set(companyKey, companyNodeId);
        nodes.push({
          id: companyNodeId,
          type: "company",
          data: {
            label: mc.name,
            nodeKind: "company" as const,
            relationshipType: "mandate" as const,
            siren: mc.siren,
            role: mc.role,
          },
          position: { x: 0, y: 0 },
        });
      }

      // Edge: person -> company
      edges.push({
        id: `e-${personId}-${companyNodeId}-${mIdx}`,
        source: personId,
        target: companyNodeId,
        style: { stroke: "#94a3b8", strokeWidth: 1.5 },
        labelStyle: { fontSize: 9, fill: "#94a3b8" },
      });
    });
  });

  // Parent company
  const parentCompanyName = resolveParentCompanyName(company.business.parentCompany);
  if (parentCompanyName) {
    const parentId = "parent";
    nodes.push({
      id: parentId,
      type: "company",
      data: {
        label: parentCompanyName,
        nodeKind: "company" as const,
        relationshipType: "parent" as const,
        siren: null,
        role: null,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: "e-parent",
      source: parentId,
      target: "center",
      style: { stroke: RELATIONSHIP_COLORS.parent, strokeWidth: 2.5 },
    });
  }

  // Acquisitions
  (rel?.acquisitions ?? []).slice(0, MAX_ACQUISITIONS).forEach((acq: CompanyAcquisition, i: number) => {
    const acqId = `acq-${i}`;
    const label = acq.acquisitionDate
      ? `${acq.companyName} (${acq.acquisitionDate.slice(0, 4)})`
      : acq.companyName;
    nodes.push({
      id: acqId,
      type: "company",
      data: {
        label,
        nodeKind: "company" as const,
        relationshipType: "acquisition" as const,
        siren: null,
        role: null,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: `e-acq-${i}`,
      source: "center",
      target: acqId,
      style: { stroke: RELATIONSHIP_COLORS.acquisition, strokeWidth: 2 },
      animated: true,
    });
  });

  // Affiliated companies derived from shared mandates (companies where 2+ directors have mandates)
  const mandateCompanyCounts = new Map<string, { name: string; siren: Nullable<string>; count: number }>();
  for (const mandate of rel?.executiveMandates ?? []) {
    for (const mc of mandate.companies) {
      const key = mc.siren ?? mc.name.toLowerCase();
      if (companyNodeKeys.has(key)) continue; // already shown as holding
      const existing = mandateCompanyCounts.get(key);
      if (existing) {
        existing.count++;
      } else {
        mandateCompanyCounts.set(key, { name: mc.name, siren: mc.siren, count: 1 });
      }
    }
  }
  const sharedMandateCompanies = Array.from(mandateCompanyCounts.values())
    .filter((mc) => mc.count >= 2)
    .slice(0, MAX_AFFILIATES);

  sharedMandateCompanies.forEach((mc, i) => {
    const affId = `aff-mandate-${i}`;
    nodes.push({
      id: affId,
      type: "company",
      data: {
        label: mc.name,
        nodeKind: "company" as const,
        relationshipType: "affiliated" as const,
        siren: mc.siren,
        role: null,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: `e-aff-mandate-${i}`,
      source: "center",
      target: affId,
      style: { stroke: RELATIONSHIP_COLORS.affiliated, strokeWidth: 1.5, strokeDasharray: "5 3" },
    });
    companyNodeKeys.set(mc.siren ?? mc.name.toLowerCase(), affId);
  });

  // Affiliated companies (from LinkedIn/Coresignal)
  const remainingAffiliateSlots = MAX_AFFILIATES - sharedMandateCompanies.length;
  (rel?.affiliatedCompanies ?? []).slice(0, Math.max(0, remainingAffiliateSlots)).forEach((url: string, i: number) => {
    const affId = `aff-${i}`;
    const nameFromUrl = url
      .replace(/https?:\/\/(www\.)?linkedin\.com\/company\//i, "")
      .replace(/\/$/, "")
      .replace(/-/g, " ");
    nodes.push({
      id: affId,
      type: "company",
      data: {
        label: nameFromUrl,
        nodeKind: "company" as const,
        relationshipType: "affiliated" as const,
        siren: null,
        role: null,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: `e-aff-${i}`,
      source: "center",
      target: affId,
      style: { stroke: RELATIONSHIP_COLORS.affiliated, strokeWidth: 1.5, strokeDasharray: "5 3" },
    });
  });

  // Domiciliation
  if (rel?.domiciliationCompany) {
    const domId = "domiciliation";
    nodes.push({
      id: domId,
      type: "company",
      data: {
        label: rel.domiciliationCompany.name,
        nodeKind: "company" as const,
        relationshipType: "domiciliation" as const,
        siren: rel.domiciliationCompany.siren,
        role: null,
      },
      position: { x: 0, y: 0 },
    });
    edges.push({
      id: "e-dom",
      source: "center",
      target: domId,
      style: { stroke: RELATIONSHIP_COLORS.domiciliation, strokeWidth: 2 },
    });
  }

  return { nodes, edges };
}

/**
 * Two-tier concentric radial layout:
 * - Center: the company
 * - Inner ring (R1): person nodes
 * - Outer ring (R2): mandate company nodes, fanned around their parent person
 * - Extra nodes (parent, acquisitions, affiliates, domiciliation) placed on a third ring
 */
export function applySpiderLayout(graphData: GraphData): GraphData {
  const { nodes, edges } = graphData;

  const centerNode = nodes.find((n) => n.id === "center");
  if (!centerNode) return graphData;

  const personNodes = nodes.filter(
    (n) => n.type === "person",
  );
  const companyNodes = nodes.filter(
    (n) => n.type === "company",
  );

  // Separate mandate companies (linked to persons) from direct companies (linked to center)
  const personIds = new Set(personNodes.map((n) => n.id));
  const mandateCompanyIds = new Set<string>();
  for (const edge of edges) {
    if (personIds.has(edge.source) && !personIds.has(edge.target)) {
      mandateCompanyIds.add(edge.target);
    }
  }

  const mandateCompanyNodes = companyNodes.filter((n) => mandateCompanyIds.has(n.id));
  const directCompanyNodes = companyNodes.filter((n) => !mandateCompanyIds.has(n.id));

  // Build person -> mandate companies mapping
  const personMandates = new Map<string, string[]>();
  for (const edge of edges) {
    if (personIds.has(edge.source) && mandateCompanyIds.has(edge.target)) {
      const list = personMandates.get(edge.source) ?? [];
      if (!list.includes(edge.target)) {
        list.push(edge.target);
      }
      personMandates.set(edge.source, list);
    }
  }

  const personCount = personNodes.length;
  const totalNodes = nodes.length;

  // Scale radii based on total node count so few nodes stay compact
  const R1 = totalNodes <= 5
    ? Math.max(90, personCount * 32)
    : Math.max(130, personCount * 32);
  const R2 = R1 + (totalNodes <= 5
    ? Math.max(70, mandateCompanyNodes.length * 16)
    : Math.max(110, mandateCompanyNodes.length * 16));
  const R3 = R2 + (totalNodes <= 5 ? 70 : 110);

  // Position center
  centerNode.position = { x: -CENTRAL_SIZE / 2, y: -CENTRAL_SIZE / 2 };

  // Distribute all first-level nodes (persons + direct companies) evenly around center
  const firstLevelNodes: { node: SpiderNode; kind: "person" | "company" }[] = [
    ...personNodes.map((n) => ({ node: n, kind: "person" as const })),
    ...directCompanyNodes.map((n) => ({ node: n, kind: "company" as const })),
  ];
  const firstLevelCount = firstLevelNodes.length;

  const firstLevelAngles = new Map<string, number>();
  firstLevelNodes.forEach(({ node, kind }, i) => {
    const angle = (2 * Math.PI * i) / Math.max(firstLevelCount, 1) - Math.PI / 2;
    firstLevelAngles.set(node.id, angle);
    const radius = kind === "person" ? R1 : R3;
    const halfW = kind === "person" ? PERSON_SIZE / 2 : COMPANY_NODE_WIDTH / 2;
    const halfH = kind === "person" ? PERSON_SIZE / 2 : COMPANY_NODE_HEIGHT / 2;
    node.position = {
      x: Math.cos(angle) * radius - halfW,
      y: Math.sin(angle) * radius - halfH,
    };
  });

  // Alias for person angles (used by mandate fan)
  const personAngles = new Map<string, number>();
  for (const { node, kind } of firstLevelNodes) {
    if (kind === "person") personAngles.set(node.id, firstLevelAngles.get(node.id)!);
  }

  // Position mandate company nodes in fan arcs around their parent person
  const positionedMandates = new Set<string>();
  for (const [personId, mandateIds] of personMandates) {
    const baseAngle = personAngles.get(personId) ?? 0;
    const count = mandateIds.length;
    const minAngularGap = (COMPANY_NODE_WIDTH + 30) / R2;
    const neededSpread = (count - 1) * minAngularGap;
    const maxSpread = (2 * Math.PI) / Math.max(firstLevelCount, 1);
    const fanSpread = Math.max(neededSpread, maxSpread * 0.8);

    mandateIds.forEach((mandateId, i) => {
      if (positionedMandates.has(mandateId)) return;
      positionedMandates.add(mandateId);

      const offset = count === 1 ? 0 : (i / (count - 1) - 0.5) * fanSpread;
      const angle = baseAngle + offset;
      const node = mandateCompanyNodes.find((n) => n.id === mandateId);
      if (node) {
        node.position = {
          x: Math.cos(angle) * R2 - COMPANY_NODE_WIDTH / 2,
          y: Math.sin(angle) * R2 - COMPANY_NODE_HEIGHT / 2,
        };
      }
    });
  }

  // Position any unpositioned mandate nodes (shared between multiple persons)
  for (const node of mandateCompanyNodes) {
    if (!positionedMandates.has(node.id)) {
      const edge = edges.find(
        (e) => personIds.has(e.source) && e.target === node.id,
      );
      const baseAngle = edge ? (personAngles.get(edge.source) ?? 0) : 0;
      node.position = {
        x: Math.cos(baseAngle) * R2 - COMPANY_NODE_WIDTH / 2,
        y: Math.sin(baseAngle) * R2 - COMPANY_NODE_HEIGHT / 2,
      };
    }
  }

  // Post-layout: resolve remaining overlaps between all company nodes
  const allCompany = [...mandateCompanyNodes, ...directCompanyNodes];
  for (let pass = 0; pass < 5; pass++) {
    let moved = false;
    for (let i = 0; i < allCompany.length; i++) {
      for (let j = i + 1; j < allCompany.length; j++) {
        const a = allCompany[i];
        const b = allCompany[j];
        const dx = b.position.x - a.position.x;
        const dy = b.position.y - a.position.y;
        const overlapX = COMPANY_NODE_WIDTH + 15 - Math.abs(dx);
        const overlapY = COMPANY_NODE_HEIGHT + 10 - Math.abs(dy);
        if (overlapX > 0 && overlapY > 0) {
          // Push apart along the axis with smaller overlap
          if (overlapX < overlapY) {
            const pushX = (overlapX / 2 + 5) * (dx >= 0 ? 1 : -1);
            a.position = { ...a.position, x: a.position.x - pushX };
            b.position = { ...b.position, x: b.position.x + pushX };
          } else {
            const pushY = (overlapY / 2 + 5) * (dy >= 0 ? 1 : -1);
            a.position = { ...a.position, y: a.position.y - pushY };
            b.position = { ...b.position, y: b.position.y + pushY };
          }
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  // Compute per-edge handle positions so each link has its own anchor point
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const getNodeSize = (n: SpiderNode): { w: number; h: number } => {
    if (n.type === "central") return { w: CENTRAL_SIZE, h: CENTRAL_SIZE };
    if (n.type === "person") return { w: PERSON_SIZE, h: PERSON_SIZE };
    return { w: COMPANY_NODE_WIDTH, h: COMPANY_NODE_HEIGHT };
  };

  /** Compute where a line from center of a node toward (dx, dy) intersects the node border, as % */
  const borderPoint = (
    n: SpiderNode,
    dx: number,
    dy: number,
  ): { top: number; left: number } => {
    const { w, h } = getNodeSize(n);
    const isCircle = n.type === "central" || n.type === "person";

    if (isCircle) {
      const r = w / 2;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      return {
        left: (0.5 + (dx / dist) * r / w) * 100,
        top: (0.5 + (dy / dist) * r / h) * 100,
      };
    }
    // Rectangle: find intersection with border
    const angle = Math.atan2(dy, dx);
    const halfW = w / 2;
    const halfH = h / 2;
    const absCos = Math.abs(Math.cos(angle));
    const absSin = Math.abs(Math.sin(angle));
    let ix: number, iy: number;
    if (absCos * halfH > absSin * halfW) {
      // Hits left or right edge
      ix = Math.sign(Math.cos(angle)) * halfW;
      iy = Math.tan(angle) * ix;
    } else {
      // Hits top or bottom edge
      iy = Math.sign(Math.sin(angle)) * halfH;
      ix = iy / Math.tan(angle);
    }
    return {
      left: ((ix + halfW) / w) * 100,
      top: ((iy + halfH) / h) * 100,
    };
  };

  // Accumulate handle positions per node
  const nodeHandles = new Map<string, HandlePositions>();
  const getHandles = (nodeId: string): HandlePositions => {
    let h = nodeHandles.get(nodeId);
    if (!h) { h = {}; nodeHandles.set(nodeId, h); }
    return h;
  };

  for (const edge of edges) {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    if (!sourceNode || !targetNode) continue;

    const sSize = getNodeSize(sourceNode);
    const tSize = getNodeSize(targetNode);
    const sCx = sourceNode.position.x + sSize.w / 2;
    const sCy = sourceNode.position.y + sSize.h / 2;
    const tCx = targetNode.position.x + tSize.w / 2;
    const tCy = targetNode.position.y + tSize.h / 2;
    const dx = tCx - sCx;
    const dy = tCy - sCy;

    const sourceHandleId = `sh-${edge.id}`;
    const targetHandleId = `th-${edge.id}`;

    getHandles(edge.source)[sourceHandleId] = borderPoint(sourceNode, dx, dy);
    getHandles(edge.target)[targetHandleId] = borderPoint(targetNode, -dx, -dy);

    edge.sourceHandle = sourceHandleId;
    edge.targetHandle = targetHandleId;
  }

  // Inject handle positions into node data
  for (const node of nodes) {
    const handles = nodeHandles.get(node.id);
    if (handles) {
      (node.data as CentralNodeData | PersonNodeData | CompanyNodeData).handlePositions = handles;
    }
  }

  return { nodes, edges };
}

export function buildCompanyRows(company: CompanyDTO): CompanyRow[] {
  const rows: CompanyRow[] = [];
  const rel = company.corporateRelationships;

  for (const hc of rel?.holdingCompanies ?? []) {
    rows.push({ name: hc.name, type: "parent", role: hc.role, siren: hc.siren, detail: null });
  }

  const parentName = resolveParentCompanyName(company.business.parentCompany);
  if (parentName) {
    rows.push({ name: parentName, type: "parent", role: null, siren: null, detail: null });
  }

  for (const acq of rel?.acquisitions ?? []) {
    rows.push({ name: acq.companyName, type: "acquisition", role: null, siren: null, detail: acq.acquisitionDate?.slice(0, 4) ?? null });
  }

  if (rel?.domiciliationCompany) {
    rows.push({ name: rel.domiciliationCompany.name, type: "domiciliation", role: null, siren: rel.domiciliationCompany.siren, detail: null });
  }

  return rows;
}
